import { useCallback, useState } from "react";
import { func, string } from "prop-types";
import { EditorContent, useEditor } from "@tiptap/react";
import Link from "@tiptap/extension-link";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import styled from "styled-components/macro";
import { rem } from "polished";

import WysiwygMenu from "./WysiwygMenu";
import WysiwygLinkModal from "./WysiwygLinkModal";

/**
 * WYSIWYG Editor
 */
function WysiwygEditor({ ariaLabeledBy, handleInput, value }) {
  const LinkExtended = Link.extend({
    addKeyboardShortcuts() {
      return {
        "Mod-k": () => handleOpenLinkModal(this.editor),
      };
    },
  });

  const editor = useEditor({
    extensions: [
      LinkExtended.configure({
        openOnClick: false,
      }),
      StarterKit,
      Underline,
    ],
    content: value,
    onUpdate: ({ editor }) => {
      handleInput({ target: { id: ariaLabeledBy, value: editor.getHTML() } });
    },
  });

  const [linkUrl, setLinkUrl] = useState("");
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);

  const handleLinkInput = e => {
    setLinkUrl(e.target.value);
  };

  const handleOpenLinkModal = useCallback(editor => {
    setIsLinkModalOpen(true);
    const prevUrl = editor?.getAttributes("link").href;
    setLinkUrl(prevUrl || "");
  }, []);

  const handleCloseLinkModal = useCallback(() => {
    setLinkUrl("");
    setIsLinkModalOpen(false);
  }, []);

  const handleLinkSave = useCallback(() => {
    if (linkUrl) {
      editor
        .chain()
        .focus()
        .extendMarkRange("link")
        .setLink({ href: linkUrl })
        .run();
    }

    handleCloseLinkModal();
  }, [editor, handleCloseLinkModal, linkUrl]);

  if (!editor) {
    return null;
  }

  return (
    <Control className="foo" id={`${ariaLabeledBy}-control`}>
      <WysiwygMenu
        editor={editor}
        onOpenLinkModal={() => handleOpenLinkModal(editor)}
      />
      <WysiwygLinkModal
        onLinkInput={handleLinkInput}
        onSaveLink={handleLinkSave}
        onClose={handleCloseLinkModal}
        linkUrl={linkUrl}
        isOpen={isLinkModalOpen}
      />
      <EditorContent aria-labelledby={ariaLabeledBy} editor={editor} />
    </Control>
  );
}

WysiwygEditor.propTypes = {
  /** Form input ID, should correspond to property in parent state */
  ariaLabeledBy: string.isRequired,
  /** Pass form input back to parent component */
  handleInput: func.isRequired,
  value: string,
};

WysiwygEditor.defaultProps = {
  value: "",
};

const Control = styled.div`
  background-color: ${({ theme }) => theme.text.backgroundColor};
  border-radius: ${rem("5px")};
  position: relative;

  .tiptap {
    font-size: ${rem("16px")};
    min-height: ${rem("112px")};
    padding: ${rem("12px")} ${rem("15px")};

    &.ProseMirror-focused {
      border-radius: 0 0 ${rem("5px")} ${rem("5px")};
      outline: dotted 0.0625rem;
      outline-offset: 0.125rem;
    }

    a {
      color: ${({ theme }) => theme.text.wysiwygToolbarColorHover};
    }

    p,
    ul {
      margin: 0;
    }
  }
`;

export default WysiwygEditor;
